import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORE_KEY = "coachscore_lang";

const en = {
  // common
  "brand.tagline": "Coaching Needs Assessment",
  "lang.en": "EN",
  "lang.tr": "TR",
  "model.4-tier": "4-tier",
  "model.6-tier": "6-tier",
  "model.4-tier.label": "4-Tier Model",
  "model.6-tier.label": "6-Tier Model",
  "priority.High": "High",
  "priority.Medium": "Medium",
  "priority.Low": "Low",

  // dimensions / cohort / 360
  "dim.summaryTitle": "Mindset · Behavior · Outcome",
  "dim.summarySub": "Which layer of change is lagging across the organization",
  "dim.mindset.desc": "How people think — awareness, intent, perspective",
  "dim.behavior.desc": "What people do — observable habits and skills",
  "dim.outcome.desc": "What it produces — results visible in the team",
  "dim.lagging": "Lagging layer",
  "dim.orgAvg": "Org average",
  "cohort.title": "Cohort Overview",
  "cohort.sub": "Every assessed level in one table — 5D phase averages, total and band",
  "cohort.level": "Level",
  "cohort.people": "People",
  "cohort.total": "Average",
  "cohort.readiness": "Readiness",
  "cohort.band": "Band",
  "cohort.orgRow": "Organization",
  "t360.enable": "Add the team's view for this level (360°)",
  "t360.enableHint": "Rate the same 15 competencies as the team experiences them. We'll show the blind spots.",
  "t360.teamView": "Team's view",
  "t360.selfView": "Leader's view",
  "t360.title": "360° Self vs Team",
  "t360.sub": "Where the leader's self-assessment differs from how the team experiences it",
  "t360.blindSpots": "Blind spots (leader rates higher)",
  "t360.hidden": "Hidden strengths (team rates higher)",
  "t360.aligned": "Self-assessment and team view are closely aligned at this level.",
  "t360.delta": "Gap",
  "t360.empty": "No team view added yet — enable 360° on a level in the assessment to see this comparison.",

  // 5D phases
  "phase.discover.title": "Discover",
  "phase.discover.sub": "Awareness: know yourself, your context and your team",
  "phase.define.title": "Define",
  "phase.define.sub": "Clarity: set the goal, communicate it, create ownership",
  "phase.develop.title": "Develop",
  "phase.develop.sub": "Growth: develop your team with a coaching approach",
  "phase.drive.title": "Drive",
  "phase.drive.sub": "Execution: turn strategy into results",
  "phase.debrief.title": "Debrief",
  "phase.debrief.sub": "Learning: close the loop with feedback",
  "dim.mindset": "Mindset",
  "dim.behavior": "Behavior",
  "dim.outcome": "Outcome",
  "band.strong": "Strength",
  "band.potential": "Improvement potential",
  "band.gap": "Development area",
  "band.strong.desc": "Role-model skills this level can pass on to others.",
  "band.potential.desc": "Baseline capability in place; targeted coaching brings quick gains.",
  "band.gap.desc": "Priority area for coaching, needs close follow-up.",

  // 15 competencies (5D model)
  "comp.self_awareness": "Self-Awareness",
  "comp.empathy": "Empathy",
  "comp.team_dynamics": "Reading Team Dynamics",
  "comp.strategic_goal": "Strategic Goal Setting",
  "comp.clear_communication": "Clear Communication",
  "comp.goal_ownership": "Creating Goal Ownership",
  "comp.coaching_skills": "Coaching Skills",
  "comp.creativity": "Creativity",
  "comp.learning_culture": "Continuous Learning Culture",
  "comp.action_orientation": "Action Orientation",
  "comp.follow_through": "Follow-Through & Commitment",
  "comp.motivation": "Motivating Others",
  "comp.feedback": "Giving & Receiving Feedback",
  "comp.self_evaluation": "Self-Evaluation",
  "comp.lessons_learned": "Learning From Experience",

  // tiers
  "tier.c_suite": "C-Suite / Executive",
  "tierShort.c_suite": "C-Suite",
  "tier.vp_director": "VP / Director",
  "tierShort.vp_director": "VP/Director",
  "tier.mid_manager": "Mid-Level Manager",
  "tierShort.mid_manager": "Mid Manager",
  "tier.junior_ic": "Junior / Individual Contributor",
  "tierShort.junior_ic": "Junior/IC",
  "tier.executive": "Executive",
  "tierShort.executive": "Executive",
  "tier.vp": "Vice President",
  "tierShort.vp": "VP",
  "tier.director": "Director",
  "tierShort.director": "Director",
  "tier.senior_manager": "Senior Manager",
  "tierShort.senior_manager": "Sr. Manager",
  "tier.team_lead": "Team Lead",
  "tierShort.team_lead": "Team Lead",
  "tier.associate": "Associate",
  "tierShort.associate": "Associate",

  // guided questionnaire (5D)
  "q.self_awareness": "Leaders at this level recognise their own strengths, weak spots and emotional reactions.",
  "q.empathy": "They understand the needs, emotions and perspectives of their team members.",
  "q.team_dynamics": "They notice and manage the relationships and roles inside their team.",
  "q.strategic_goal": "They set long-term goals and align them with the company's vision.",
  "q.clear_communication": "They communicate goals and expectations in clear, understandable language.",
  "q.goal_ownership": "They get their team genuinely committed to the agreed goals.",
  "q.coaching_skills": "They use a coaching approach to develop their team members.",
  "q.creativity": "They generate new ideas and unconventional approaches, and encourage the team to do the same.",
  "q.learning_culture": "They create a safe environment where learning and experimenting are welcome.",
  "q.action_orientation": "They turn strategy into concrete actions and produce results.",
  "q.follow_through": "They follow through on commitments fully and on time.",
  "q.motivation": "They mobilise themselves and their team towards the goal.",
  "q.feedback": "They give constructive feedback and are open to receiving it.",
  "q.self_evaluation": "They assess their own performance objectively.",
  "q.lessons_learned": "They draw lessons from experience and make the improvement stick.",
  "agree.1": "Strongly Disagree",
  "agree.2": "Disagree",
  "agree.3": "Neutral",
  "agree.4": "Agree",
  "agree.5": "Strongly Agree",

  // how it works
  "hiw.cta": "How it works",
  "hiw.title": "How to fill in the scorecard",
  "hiw.subtitle": "Five short steps, about five minutes — here's exactly what to expect.",
  "hiw.videoSoon": "Intro video coming soon — walk through the steps below in the meantime.",
  "hiw.start": "Start the assessment",
  "hiw.s1.title": "1. Describe your organization",
  "hiw.s1.desc": "Enter your company name, pick a 4-level or 6-level seniority structure and optionally add a line of context about your business.",
  "hiw.s2.title": "2. Rate each seniority level",
  "hiw.s2.desc": "One step per level: rate 15 statements grouped into the five 5D phases — Discover, Define, Develop, Drive, Debrief. There are no wrong answers; answer for today, not for the ideal.",
  "hiw.s3.title": "3. Add how many people are at each level",
  "hiw.s3.desc": "Headcount lets us size each program correctly for that level, so enter your best estimate.",
  "hiw.s4.title": "4. Generate your plan",
  "hiw.s4.desc": "On the last step press \"Finish & generate plan\". Our AI builds a program per level with format, modules and a timeline in under a minute.",
  "hiw.s5.title": "5. Review and save your report",
  "hiw.s5.desc": "Explore the dashboard, recommendations and report tabs, then use \"Save & Download\" to get the full report in your inbox.",

  // landing
  "landing.badge": "Free Leadership Coaching Scorecard",
  "landing.headline": "Find out where your leaders need coaching — in 5 minutes.",
  "landing.sub": "Assess leadership readiness across every level of your organization with the CoachMeUpp 5D model and get an AI-generated coaching program with timelines and a rollout roadmap — instantly.",
  "landing.check1": "Free — no signup required to see your results",
  "landing.check2": "Takes about 5 minutes",
  "landing.check3": "Works for a 4-tier or 6-tier org structure",
  "landing.cta": "Start free assessment",
  "landing.ctaNote": "No credit card · No email to view results",
  "landing.vp1.title": "Pinpoint the gaps",
  "landing.vp1.desc": "Rate the 15 competencies of the CoachMeUpp 5D model at every seniority level to see exactly where coaching is needed.",
  "landing.vp2.title": "AI-built coaching plan",
  "landing.vp2.desc": "Get a tailored program for each level — format, modules and 5D phases, generated in seconds.",
  "landing.vp3.title": "12-month roadmap",
  "landing.vp3.desc": "A sequenced rollout timeline shows what to run when, so you can act on the plan immediately.",
  "landing.vp4.title": "Executive dashboard",
  "landing.vp4.desc": "Radar charts, readiness scores and a printable report you can bring straight to the leadership table.",
  "landing.statReadiness": "Org Readiness",
  "landing.statTopGap": "Top Gap",
  "landing.statPrograms": "Programs",
  "landing.statGapValue": "People Mgmt",
  "landing.heroAlt": "Executive coaching session",

  // app shell
  "app.generate": "Generate AI Plan",
  "app.generating": "Generating…",
  "app.saveDownload": "Email my report",
  "app.print": "Download PDF",
  "print.blocked": "This report is sent to you by email. Printing from the browser is disabled — please use the PDF in your inbox.",
  "pdf.priorityPrograms": "Priority programs",
  "pdf.bookingNote": "Scan the code or open the link to pick a time and walk through these results together.",
  "pdf.coverFooter": "Prepared by {brand} · 5D Leadership Development Model",
  "share.loading": "Loading your report…",
  "share.expiredTitle": "This report link has expired",
  "share.expiredDesc": "Private report links stay active for 30 days. Run the scorecard again to get a fresh report.",
  "share.notFoundTitle": "Report not found",
  "share.notFoundDesc": "Please check the link in your email, or run the scorecard again.",
  "share.startAgain": "Run the scorecard",
  "share.badge": "Your private report",
  "share.validUntil": "Link valid until {date}",
  "app.preparingPdf": "Preparing PDF…",
  "app.pdfReady": "PDF downloaded",
  "app.pdfFailed": "Could not create the PDF. Please try again.",
  "tab.scorecard": "Assessment",
  "tab.dashboard": "Dashboard",
  "tab.recommendations": "Recommendations",
  "tab.report": "Report",
  "toast.generated": "AI coaching recommendations generated",
  "toast.generateFailed": "Failed to generate recommendations. Please try again.",
  "toast.saved": "Saved — your report is on its way to your inbox",
  "app.preparingReport": "Preparing your report…",
  "app.reportEmailed": "Report sent to your email",
  "app.reportEmailFailed": "We could not prepare the report file — we have emailed your report without it.",
  "toast.rateLimit": "Too many submissions — please try again later.",
  "toast.error": "Something went wrong. Please try again.",
  "empty.title": "No recommendations yet",
  "empty.desc": "Complete the guided assessment, then generate a tailored AI coaching plan with programs and timelines for every seniority level.",
  "empty.cta": "Generate AI Plan",

  // guided stepper
  "step.counter": "Step {current} of {total}",
  "step.percent": "{pct}% complete",
  "step.back": "Back",
  "step.next": "Next",
  "step.finish": "Finish & generate plan",
  "step.companyTitle": "Tell us about your organization",
  "step.companyDesc": "We use this to personalize your coaching plan and report.",
  "step.companyLabel": "Company name",
  "step.companyPlaceholder": "Acme Corporation",
  "step.modelLabel": "Seniority structure",
  "step.modelHint": "How many leadership levels should we assess?",
  "step.contextLabel": "Organizational context (optional)",
  "step.contextHint": "The more context you give, the sharper the AI recommendations.",
  "step.contextPlaceholder": "e.g. Fast-scaling fintech, many first-time managers, preparing for Series C…",
  "step.tierTitle": "{tier}",
  "step.tierDesc": "Rate each 5D statement for this level as it is today.",
  "step.phaseAvg": "Phase average {value}/5",
  "step.headcount": "People at this level",
  "step.headcountHint": "Helps us size the program for this level.",
  "step.avg": "avg {value}/5",
  "step.answered": "{n} of {total} answered",
  "step.reviewHint": "You can revisit any step before generating your plan.",

  // dashboard
  "dash.kpiReadiness": "Org Readiness",
  "dash.kpiReadinessSub": "Leadership readiness index",
  "dash.kpiTopGap": "Top Gap",
  "dash.kpiTopGapSub": "Weakest org competency",
  "dash.kpiLevels": "Levels Assessed",
  "dash.kpiLevelsSub": "{model} model",
  "dash.kpiHigh": "High Priority",
  "dash.kpiHighSub": "Urgent programs",
  "dash.kpiHighEmpty": "Generate to populate",
  "dash.radarTitle": "5D Phase Radar",
  "dash.radarSub": "Average score (0-5) per 5D phase across seniority levels",
  "dash.readinessTitle": "Readiness by Seniority",
  "dash.readinessSub": "Average leadership readiness per level",
  "dash.timelineTitle": "Coaching Rollout Timeline",
  "dash.timelineSub": "12-month program plan across seniority levels",
  "dash.timelineEmpty": "Generate AI recommendations to visualize the coaching rollout timeline.",

  // gantt
  "gantt.program": "Program",
  "gantt.legend": "{priority} priority",
  "gantt.bar": "{months}mo · {format}",

  // recommendations
  "rec.aiSummary": "AI Executive Summary",
  "rec.priorityGaps": "Priority gaps:",
  "rec.format": "Format",
  "rec.duration": "Duration",
  "rec.months": "{n} months",
  "rec.modules": "Curriculum Modules",
  "rec.phases": "Timeline Phases",

  // report
  "report.kicker": "Coaching Needs Scorecard · Executive Report",
  "report.modelLine": "{model} seniority model",
  "report.execSummary": "Executive Summary",
  "report.orgReadiness": "Org Readiness",
  "report.scorecard": "5D Competency Scorecard",
  "report.scorecardSub": "Score 0–5 per competency, grouped into the five 5D phases (mindset / behavior / outcome).",
  "report.competency": "Competency",
  "report.levelAverage": "Level average",
  "report.level": "Seniority Level",
  "report.priority": "{priority} priority",
  "report.durationShort": "{n} mo",
  "report.book": "Book a call",
  "report.empty": "Generate AI recommendations first to include a coaching plan in the report.",

  // lead capture
  "lead.title": "Get your report by email",
  "lead.desc": "Tell us where to send it. We'll email you the full coaching plan as a branded PDF report.",
  "lead.name": "Full name",
  "lead.namePh": "Jane Doe",
  "lead.email": "Work email",
  "lead.emailPh": "jane@company.com",
  "lead.company": "Company",
  "lead.companyPh": "Acme Corporation",
  "lead.role": "Role",
  "lead.rolePh": "Head of People",
  "lead.size": "Company size",
  "lead.sizePh": "Select",
  "lead.employees": "employees",
  "lead.submit": "Send my report",
  "lead.saving": "Saving…",
  "lead.footnote": "We'll only use your details to share your coaching results and follow up.",
  "lead.required": "Required",
  "lead.invalidEmail": "Enter a valid email",

  // admin
  "admin.accessTitle": "Admin Access",
  "admin.accessDesc": "Enter the passcode to view captured leads",
  "admin.passcodePh": "Passcode",
  "admin.unlock": "Unlock",
  "admin.verifying": "Verifying…",
  "admin.invalid": "Invalid passcode",
  "admin.title": "CoachScore Admin",
  "admin.subtitle": "Lead & scorecard console",
  "admin.branding": "Branding",
  "admin.lock": "Lock",
  "admin.tabLeads": "Leads",
  "admin.tabCompare": "Compare",
  "admin.reportTitle": "{company} — Coaching Report",
  "admin.contact": "Contact:",
  "admin.employees": "employees",
  "admin.print": "Download PDF",
  "admin.pdfTooltip": "Download PDF report",

  // library
  "lib.loading": "Loading leads…",
  "lib.emptyTitle": "No leads captured yet",
  "lib.emptyDesc": "When a prospect completes the scorecard and saves their report, their contact details and coaching plan appear here.",
  "lib.title": "Captured Leads",
  "lib.count": "{n} lead(s) · select any two to compare",
  "lib.export": "Export CSV",
  "lib.refresh": "Refresh",
  "lib.compare": "Compare ({n}/2)",
  "lib.aiPlan": "AI plan",
  "lib.noAiPlan": "No AI plan",
  "lib.converted": "Converted",
  "lib.captured": "Captured {date}",
  "lib.readiness": "Readiness",
  "lib.programs": "Programs",
  "lib.viewReport": "View Report",
  "lib.markConverted": "Mark as converted (stops follow-ups)",
  "lib.markNotConverted": "Mark as not converted",
  "lib.loadFailed": "Failed to load leads",
  "lib.deleted": "Lead deleted",
  "lib.deleteFailed": "Delete failed",
  "lib.updateFailed": "Update failed",
  "lib.markedConverted": "Marked as converted",
  "lib.markedNot": "Marked as not converted",
  "lib.exported": "Exported {n} lead(s) to CSV",

  // compare
  "cmp.emptyTitle": "Nothing to compare yet",
  "cmp.emptyDesc": "Go to the Leads tab, select two captured leads and click Compare to see them side by side.",
  "cmp.company": "Company {letter}",
  "cmp.kpiReadiness": "Org Readiness",
  "cmp.kpiHigh": "High-Priority Programs",
  "cmp.avgTitle": "5D Phase Averages",
  "cmp.avgSub": "Average score (0-5) per 5D phase across all levels",
  "cmp.byLevelTitle": "Readiness by Level",
  "cmp.byLevelSub": "Both scorecards use the {model} model — level-by-level comparison",
  "cmp.level": "Level",
  "cmp.delta": "Delta (B−A)",
  "cmp.diffModels": "These scorecards use different seniority models ({a} vs {b}), so level-by-level comparison isn't available. Competency averages above still compare directly.",

  // settings
  "set.accessTitle": "Owner Access",
  "set.accessDesc": "Enter the admin passcode to edit branding",
  "set.backToApp": "Back to app",
  "set.title": "Branding Settings",
  "set.subtitle": "Customize your assessment",
  "set.save": "Save changes",
  "set.saved": "Settings saved",
  "set.saveFailed": "Failed to save settings",
  "set.unauthorized": "Unauthorized — check passcode",
  "set.loading": "Loading…",
  "set.identity": "Identity",
  "set.brandName": "Brand name",
  "set.logo": "Logo",
  "set.noLogo": "No logo",
  "set.upload": "Upload",
  "set.logoHint": "PNG, JPG, SVG or WebP · up to 5MB",
  "set.logoUploaded": "Logo uploaded",
  "set.logoFailed": "Logo upload failed",
  "set.brandColor": "Brand color",
  "set.landingCopy": "Landing copy",
  "set.introVideo": "Intro video link",
  "set.introVideoHint": "Paste a YouTube, Vimeo or MP4 link. It replaces the animated \"How it works\" steps on the landing page. Leave empty to keep the animated version.",
  "set.headline": "Headline",
  "set.subheadline": "Subheadline",
  "set.reportEmail": "Report email",
  "set.previewEmail": "Preview email",
  "set.previewFailed": "Failed to load email preview",
  "set.intro": "Intro message",
  "set.introHint": "Shown at the top of the report email. Use {name} and {company} as placeholders.",
  "set.bookingLink": "Booking link",
  "set.bookingHint": "Added as a \"Book a call\" button in report emails, follow-ups and the report page.",
  "set.followupsToggle": "Send automated day-3 / day-7 follow-up emails to leads who haven't converted",
  "set.followups": "Follow-up emails",
  "set.followupsHint": "Sent to leads who haven't been marked converted. Use {name}, {company} and {gap} placeholders. A \"Book a call\" button is added automatically.",
  "set.day3": "Day 3 nudge",
  "set.day7": "Day 7 nudge",
  "set.preview": "Preview",
  "set.subjectPh": "Subject line",
  "set.bodyPh": "Email body",
  "set.livePreview": "Live preview",
  "set.previewBadge": "Free Scorecard",
  "set.previewHeadlinePh": "Your headline here",
  "set.previewSubPh": "Your subheadline here",
  "set.previewCta": "Start free assessment",
  "set.emailPreviewTitle": "Email preview",
  "set.emailPreviewNote": "Preview uses sample data (Jane Doe / Acme Corporation). Live emails use each prospect's real results.",
  "set.langEn": "English",
  "set.langTr": "Turkish",
};

const tr = {
  "brand.tagline": "Koçluk İhtiyaç Değerlendirmesi",
  "lang.en": "EN",
  "lang.tr": "TR",
  "model.4-tier": "4 seviyeli",
  "model.6-tier": "6 seviyeli",
  "model.4-tier.label": "4 Seviyeli Model",
  "model.6-tier.label": "6 Seviyeli Model",
  "priority.High": "Yüksek",
  "priority.Medium": "Orta",
  "priority.Low": "Düşük",

  "dim.summaryTitle": "Zihniyet · Davranış · Sonuç",
  "dim.summarySub": "Kurum genelinde değişimin hangi katmanı geride kalıyor",
  "dim.mindset.desc": "Nasıl düşünüyorlar — farkındalık, niyet, bakış açısı",
  "dim.behavior.desc": "Ne yapıyorlar — gözlenebilir alışkanlıklar ve beceriler",
  "dim.outcome.desc": "Ne üretiyor — ekipte görünen sonuçlar",
  "dim.lagging": "Geride kalan katman",
  "dim.orgAvg": "Kurum ortalaması",
  "cohort.title": "Kohort Özeti",
  "cohort.sub": "Değerlendirilen tüm seviyeler tek tabloda — 5D faz ortalamaları, genel ortalama ve bant",
  "cohort.level": "Seviye",
  "cohort.people": "Kişi",
  "cohort.total": "Ortalama",
  "cohort.readiness": "Hazırlık",
  "cohort.band": "Bant",
  "cohort.orgRow": "Kurum",
  "t360.enable": "Bu seviye için ekip görüşünü de ekle (360°)",
  "t360.enableHint": "Aynı 15 yetkinliği ekibin deneyimlediği şekilde puanlayın. Kör noktaları size gösteririz.",
  "t360.teamView": "Ekip görüşü",
  "t360.selfView": "Yönetici görüşü",
  "t360.title": "360° Yönetici ve Ekip Karşılaştırması",
  "t360.sub": "Yöneticinin öz değerlendirmesi ile ekibin deneyimi arasındaki farklar",
  "t360.blindSpots": "Kör noktalar (yönetici daha yüksek puanlıyor)",
  "t360.hidden": "Gizli güçler (ekip daha yüksek puanlıyor)",
  "t360.aligned": "Bu seviyede öz değerlendirme ile ekip görüşü büyük ölçüde örtüşüyor.",
  "t360.delta": "Fark",
  "t360.empty": "Henüz ekip görüşü eklenmedi — karşılaştırmayı görmek için değerlendirmede bir seviyede 360°'yi açın.",

  "phase.discover.title": "Discover · Keşfet",
  "phase.discover.sub": "Farkındalık: kendini, çevreni ve ekibini tanı",
  "phase.define.title": "Define · Tanımla",
  "phase.define.sub": "Netlik: hedefi kur, aktar, sahiplendir",
  "phase.develop.title": "Develop · Geliştir",
  "phase.develop.sub": "Büyüme: ekibini koçluk yaklaşımıyla geliştir",
  "phase.drive.title": "Drive · Harekete Geçir",
  "phase.drive.sub": "Uygulama: stratejiyi sonuca dönüştür",
  "phase.debrief.title": "Debrief · Bilgilendir",
  "phase.debrief.sub": "Öğrenme: geri bildirimle döngüyü kapat",
  "dim.mindset": "Zihniyet",
  "dim.behavior": "Davranış",
  "dim.outcome": "Sonuç",
  "band.strong": "Güçlü Alan",
  "band.potential": "İyileştirme Potansiyeli",
  "band.gap": "Gelişim Alanı",
  "band.strong.desc": "Bu seviyenin kurum içinde rol model olabileceği, başkalarına aktarabileceği beceriler.",
  "band.potential.desc": "Temel yeterlilik mevcut; hedefli koçlukla kısa sürede belirgin gelişim mümkün.",
  "band.gap.desc": "Koçluk sürecinde öncelikli ele alınacak, yakın takip gerektiren alanlar.",

  "comp.self_awareness": "Öz-Farkındalık",
  "comp.empathy": "Empati",
  "comp.team_dynamics": "Takım Dinamiklerini Gözlemleme",
  "comp.strategic_goal": "Stratejik Hedef Belirleme",
  "comp.clear_communication": "Net İletişim",
  "comp.goal_ownership": "Hedef Sahiplenmesini Sağlama",
  "comp.coaching_skills": "Koçluk Becerileri",
  "comp.creativity": "Yaratıcılık",
  "comp.learning_culture": "Sürekli Öğrenme Kültürü",
  "comp.action_orientation": "Aksiyon Odaklılık",
  "comp.follow_through": "Takip ve Taahhüt",
  "comp.motivation": "Motivasyon Sağlama",
  "comp.feedback": "Geri Bildirim Verme ve Alma",
  "comp.self_evaluation": "Öz Değerlendirme",
  "comp.lessons_learned": "Süreçten Ders Çıkarma",

  "tier.c_suite": "Üst Yönetim (C-Seviyesi)",
  "tierShort.c_suite": "Üst Yönetim",
  "tier.vp_director": "Genel Müdür Yardımcısı / Direktör",
  "tierShort.vp_director": "GMY/Direktör",
  "tier.mid_manager": "Orta Düzey Yönetici",
  "tierShort.mid_manager": "Orta Düzey",
  "tier.junior_ic": "Uzman / Bireysel Katkı Sağlayan",
  "tierShort.junior_ic": "Uzman",
  "tier.executive": "Üst Düzey Yönetici",
  "tierShort.executive": "Üst Düzey",
  "tier.vp": "Başkan Yardımcısı",
  "tierShort.vp": "Bşk. Yrd.",
  "tier.director": "Direktör",
  "tierShort.director": "Direktör",
  "tier.senior_manager": "Kıdemli Yönetici",
  "tierShort.senior_manager": "Kd. Yönetici",
  "tier.team_lead": "Takım Lideri",
  "tierShort.team_lead": "Takım Lideri",
  "tier.associate": "Uzman",
  "tierShort.associate": "Uzman",

  "q.self_awareness": "Bu seviyedeki yöneticiler kendi güçlü yönlerini, zayıf noktalarını ve duygusal tepkilerini tanır.",
  "q.empathy": "Ekip üyelerinin ihtiyaçlarını, duygularını ve bakış açılarını anlarlar.",
  "q.team_dynamics": "Ekip içindeki ilişkileri ve rolleri fark edip yönetirler.",
  "q.strategic_goal": "Uzun vadeli hedefler belirler ve bunları kurumun vizyonuyla hizalarlar.",
  "q.clear_communication": "Hedef ve beklentileri açık, anlaşılır bir dille aktarırlar.",
  "q.goal_ownership": "Ekibin belirlenen hedeflere gerçek anlamda bağlılık geliştirmesini sağlarlar.",
  "q.coaching_skills": "Ekip üyelerinin gelişimini desteklemek için koçluk yaklaşımını kullanırlar.",
  "q.creativity": "Yeni fikirler ve alışılmışın dışında yaklaşımlar üretir, ekipte de bunu teşvik ederler.",
  "q.learning_culture": "Ekipte öğrenmeye ve denemeye açık, güvenli bir ortam oluştururlar.",
  "q.action_orientation": "Stratejiyi somut aksiyonlara dönüştürüp sonuç üretirler.",
  "q.follow_through": "Taahhüt edilen işleri zamanında ve eksiksiz yerine getirirler.",
  "q.motivation": "Kendilerini ve ekiplerini hedefe doğru harekete geçirirler.",
  "q.feedback": "Yapıcı geri bildirim verir ve geri bildirim almaya açıktırlar.",
  "q.self_evaluation": "Kendi performanslarını objektif olarak değerlendirirler.",
  "q.lessons_learned": "Deneyimlerden ders çıkarıp gelişimi kalıcı hale getirirler.",
  "agree.1": "Kesinlikle katılmıyorum",
  "agree.2": "Katılmıyorum",
  "agree.3": "Kararsızım",
  "agree.4": "Katılıyorum",
  "agree.5": "Kesinlikle katılıyorum",

  "hiw.cta": "Nasıl çalışır?",
  "hiw.title": "Karneyi nasıl dolduracaksınız",
  "hiw.subtitle": "Beş kısa adım, yaklaşık beş dakika — sizi neyin beklediğini adım adım anlatıyoruz.",
  "hiw.videoSoon": "Tanıtım videosu çok yakında — o zamana kadar aşağıdaki adımları inceleyebilirsiniz.",
  "hiw.start": "Değerlendirmeye başla",
  "hiw.s1.title": "1. Kurumunuzu tanıtın",
  "hiw.s1.desc": "Şirket adınızı girin, 4 veya 6 seviyeli kıdem yapısını seçin ve isterseniz işinizle ilgili kısa bir bağlam ekleyin.",
  "hiw.s2.title": "2. Her kıdem seviyesini puanlayın",
  "hiw.s2.desc": "Her seviye için bir adım: 15 ifadeyi beş 5D fazına göre gruplanmış hâlde puanlayın — Keşfet, Tanımla, Geliştir, Harekete Geçir, Bilgilendir. Doğru ya da yanlış cevap yok; ideali değil, bugünü düşünerek yanıtlayın.",
  "hiw.s3.title": "3. Her seviyedeki kişi sayısını girin",
  "hiw.s3.desc": "Bu sayı, programı o seviye için doğru ölçeklendirmemizi sağlar; en iyi tahmininizi yazmanız yeterli.",
  "hiw.s4.title": "4. Planınızı oluşturun",
  "hiw.s4.desc": "Son adımda \"Bitir ve planı oluştur\" düğmesine basın. Yapay zekâ bir dakika içinde her seviye için format, modüller ve zaman planı içeren bir program hazırlar.",
  "hiw.s5.title": "5. Sonuçları inceleyin ve kaydedin",
  "hiw.s5.desc": "Pano, öneriler ve rapor sekmelerini gezin; ardından \"Kaydet ve İndir\" ile raporun tamamını e-posta adresinize alın.",

  "landing.badge": "Ücretsiz Liderlik Koçluğu Karnesi",
  "landing.headline": "Liderlerinizin nerede koçluğa ihtiyacı olduğunu 5 dakikada keşfedin.",
  "landing.sub": "Organizasyonunuzun her seviyesindeki liderlik hazırlığını CoachMeUpp 5D modeliyle değerlendirin; zaman planı ve uygulama yol haritasıyla yapay zekâ destekli bir koçluk programını anında alın.",
  "landing.check1": "Ücretsiz — sonuçları görmek için kayıt gerekmez",
  "landing.check2": "Yaklaşık 5 dakika sürer",
  "landing.check3": "4 veya 6 seviyeli organizasyon yapısına uygundur",
  "landing.cta": "Ücretsiz değerlendirmeye başla",
  "landing.ctaNote": "Kredi kartı yok · Sonuçları görmek için e-posta gerekmez",
  "landing.vp1.title": "Açıkları net görün",
  "landing.vp1.desc": "CoachMeUpp 5D modelinin 15 yetkinliğini her kıdem seviyesinde puanlayın; koçluğun tam olarak nerede gerektiğini görün.",
  "landing.vp2.title": "Yapay zekâ ile koçluk planı",
  "landing.vp2.desc": "Her seviye için özel program: format, modüller ve 5D fazları — saniyeler içinde.",
  "landing.vp3.title": "12 aylık yol haritası",
  "landing.vp3.desc": "Sıralanmış uygulama takvimi neyin ne zaman yapılacağını gösterir; plana hemen geçebilirsiniz.",
  "landing.vp4.title": "Yönetim panosu",
  "landing.vp4.desc": "Radar grafikleri, hazırlık puanları ve doğrudan yönetim masasına götürebileceğiniz yazdırılabilir rapor.",
  "landing.statReadiness": "Kurum Hazırlığı",
  "landing.statTopGap": "En Büyük Açık",
  "landing.statPrograms": "Program",
  "landing.statGapValue": "İnsan Yönetimi",
  "landing.heroAlt": "Yönetici koçluğu görüşmesi",

  "app.generate": "Yapay Zekâ Planı Oluştur",
  "app.generating": "Oluşturuluyor…",
  "app.saveDownload": "Raporu e-postala",
  "app.print": "PDF İndir",
  "print.blocked": "Bu rapor size e-posta ile gönderilir. Tarayıcıdan yazdırma kapalıdır — lütfen e-postanızdaki PDF'i kullanın.",
  "pdf.priorityPrograms": "Öncelikli programlar",
  "pdf.bookingNote": "Kodu okutun veya bağlantıyı açın; uygun bir zaman seçip sonuçları birlikte ele alalım.",
  "pdf.coverFooter": "{brand} tarafından hazırlandı · 5D Liderlik Gelişim Modeli",
  "share.loading": "Raporunuz yükleniyor…",
  "share.expiredTitle": "Bu rapor bağlantısının süresi doldu",
  "share.expiredDesc": "Özel rapor bağlantıları 30 gün geçerlidir. Yeni bir rapor için karneyi tekrar doldurun.",
  "share.notFoundTitle": "Rapor bulunamadı",
  "share.notFoundDesc": "Lütfen e-postanızdaki bağlantıyı kontrol edin veya karneyi tekrar doldurun.",
  "share.startAgain": "Karneyi doldur",
  "share.badge": "Özel raporunuz",
  "share.validUntil": "Bağlantı {date} tarihine kadar geçerli",
  "app.preparingPdf": "PDF hazırlanıyor…",
  "app.pdfReady": "PDF indirildi",
  "app.pdfFailed": "PDF oluşturulamadı. Lütfen tekrar deneyin.",
  "tab.scorecard": "Değerlendirme",
  "tab.dashboard": "Pano",
  "tab.recommendations": "Öneriler",
  "tab.report": "Rapor",
  "toast.generated": "Yapay zekâ koçluk önerileri oluşturuldu",
  "toast.generateFailed": "Öneriler oluşturulamadı. Lütfen tekrar deneyin.",
  "toast.saved": "Kaydedildi — raporunuz e-posta adresinize gönderiliyor",
  "app.preparingReport": "Raporunuz hazırlanıyor…",
  "app.reportEmailed": "Rapor e-posta adresinize gönderildi",
  "app.reportEmailFailed": "Rapor dosyası hazırlanamadı — raporunuz dosya eki olmadan e-postayla gönderildi.",
  "toast.rateLimit": "Çok fazla gönderim — lütfen daha sonra tekrar deneyin.",
  "toast.error": "Bir şeyler ters gitti. Lütfen tekrar deneyin.",
  "empty.title": "Henüz öneri yok",
  "empty.desc": "Rehberli değerlendirmeyi tamamlayın; her kıdem seviyesi için programlar ve zaman planı içeren özel bir yapay zekâ koçluk planı oluşturun.",
  "empty.cta": "Yapay Zekâ Planı Oluştur",

  "step.counter": "Adım {current} / {total}",
  "step.percent": "%{pct} tamamlandı",
  "step.back": "Geri",
  "step.next": "İleri",
  "step.finish": "Bitir ve planı oluştur",
  "step.companyTitle": "Bize kurumunuzdan bahsedin",
  "step.companyDesc": "Bu bilgileri koçluk planınızı ve raporunuzu kişiselleştirmek için kullanıyoruz.",
  "step.companyLabel": "Şirket adı",
  "step.companyPlaceholder": "Örnek Holding A.Ş.",
  "step.modelLabel": "Kıdem yapısı",
  "step.modelHint": "Kaç liderlik seviyesini değerlendirelim?",
  "step.contextLabel": "Kurumsal bağlam (opsiyonel)",
  "step.contextHint": "Ne kadar bağlam verirseniz yapay zekâ önerileri o kadar isabetli olur.",
  "step.contextPlaceholder": "örn. Hızlı büyüyen bir fintech, çok sayıda ilk kez yönetici olan kişi, Seri C hazırlığı…",
  "step.tierTitle": "{tier}",
  "step.tierDesc": "Bu seviyeyi bugünkü hâliyle her 5D ifadesi için puanlayın.",
  "step.phaseAvg": "Faz ortalaması {value}/5",
  "step.headcount": "Bu seviyedeki kişi sayısı",
  "step.headcountHint": "Programı bu seviye için doğru ölçeklendirmemize yardımcı olur.",
  "step.avg": "ortalama {value}/5",
  "step.answered": "{total} ifadeden {n} tanesi yanıtlandı",
  "step.reviewHint": "Planınızı oluşturmadan önce her adıma geri dönebilirsiniz.",

  "dash.kpiReadiness": "Kurum Hazırlığı",
  "dash.kpiReadinessSub": "Liderlik hazırlık endeksi",
  "dash.kpiTopGap": "En Büyük Açık",
  "dash.kpiTopGapSub": "En zayıf kurumsal yetkinlik",
  "dash.kpiLevels": "Değerlendirilen Seviye",
  "dash.kpiLevelsSub": "{model} model",
  "dash.kpiHigh": "Yüksek Öncelik",
  "dash.kpiHighSub": "Acil programlar",
  "dash.kpiHighEmpty": "Doldurmak için plan oluşturun",
  "dash.radarTitle": "5D Faz Radarı",
  "dash.radarSub": "Kıdem seviyelerine göre faz ortalamaları (0-5)",
  "dash.readinessTitle": "Kıdeme Göre Hazırlık",
  "dash.readinessSub": "Seviye başına ortalama liderlik hazırlığı",
  "dash.timelineTitle": "Koçluk Uygulama Takvimi",
  "dash.timelineSub": "Kıdem seviyeleri için 12 aylık program planı",
  "dash.timelineEmpty": "Koçluk uygulama takvimini görmek için yapay zekâ önerilerini oluşturun.",

  "gantt.program": "Program",
  "gantt.legend": "{priority} öncelik",
  "gantt.bar": "{months} ay · {format}",

  "rec.aiSummary": "Yapay Zekâ Yönetici Özeti",
  "rec.priorityGaps": "Öncelikli açıklar:",
  "rec.format": "Format",
  "rec.duration": "Süre",
  "rec.months": "{n} ay",
  "rec.modules": "Eğitim Modülleri",
  "rec.phases": "Zaman Planı Fazları",

  "report.kicker": "Koçluk İhtiyaç Karnesi · Yönetici Raporu",
  "report.modelLine": "{model} kıdem modeli",
  "report.execSummary": "Yönetici Özeti",
  "report.orgReadiness": "Kurum Hazırlığı",
  "report.scorecard": "5D Yetkinlik Karnesi",
  "report.scorecardSub": "Beş 5D fazına göre gruplanmış, yetkinlik başına 0–5 puan (zihniyet / davranış / sonuç).",
  "report.competency": "Yetkinlik",
  "report.levelAverage": "Seviye ortalaması",
  "report.level": "Kıdem Seviyesi",
  "report.priority": "{priority} öncelik",
  "report.durationShort": "{n} ay",
  "report.book": "Görüşme planla",
  "report.empty": "Rapora koçluk planı eklemek için önce yapay zekâ önerilerini oluşturun.",

  "lead.title": "Raporunuzu e-posta ile alın",
  "lead.desc": "Nereye göndereceğimizi söyleyin. Koçluk planınızın tamamını markalı PDF raporu olarak e-posta ile göndereceğiz.",
  "lead.name": "Ad soyad",
  "lead.namePh": "Ayşe Yılmaz",
  "lead.email": "Kurumsal e-posta",
  "lead.emailPh": "ayse@sirket.com",
  "lead.company": "Şirket",
  "lead.companyPh": "Örnek Holding A.Ş.",
  "lead.role": "Görev",
  "lead.rolePh": "İnsan Kaynakları Direktörü",
  "lead.size": "Şirket büyüklüğü",
  "lead.sizePh": "Seçin",
  "lead.employees": "çalışan",
  "lead.submit": "Raporumu gönder",
  "lead.saving": "Kaydediliyor…",
  "lead.footnote": "Bilgilerinizi yalnızca koçluk sonuçlarınızı paylaşmak ve size dönüş yapmak için kullanırız.",
  "lead.required": "Zorunlu",
  "lead.invalidEmail": "Geçerli bir e-posta girin",

  "admin.accessTitle": "Yönetici Erişimi",
  "admin.accessDesc": "Kaydedilen adayları görmek için şifreyi girin",
  "admin.passcodePh": "Şifre",
  "admin.unlock": "Kilidi aç",
  "admin.verifying": "Doğrulanıyor…",
  "admin.invalid": "Geçersiz şifre",
  "admin.title": "CoachScore Yönetim",
  "admin.subtitle": "Aday ve karne konsolu",
  "admin.branding": "Marka",
  "admin.lock": "Kilitle",
  "admin.tabLeads": "Adaylar",
  "admin.tabCompare": "Karşılaştır",
  "admin.reportTitle": "{company} — Koçluk Raporu",
  "admin.contact": "İletişim:",
  "admin.employees": "çalışan",
  "admin.print": "PDF İndir",
  "admin.pdfTooltip": "PDF raporunu indir",

  "lib.loading": "Adaylar yükleniyor…",
  "lib.emptyTitle": "Henüz aday yok",
  "lib.emptyDesc": "Bir aday karneyi tamamlayıp raporunu kaydettiğinde iletişim bilgileri ve koçluk planı burada görünür.",
  "lib.title": "Kaydedilen Adaylar",
  "lib.count": "{n} aday · karşılaştırmak için iki tanesini seçin",
  "lib.export": "CSV indir",
  "lib.refresh": "Yenile",
  "lib.compare": "Karşılaştır ({n}/2)",
  "lib.aiPlan": "YZ planı",
  "lib.noAiPlan": "YZ planı yok",
  "lib.converted": "Dönüştü",
  "lib.captured": "{date} tarihinde kaydedildi",
  "lib.readiness": "Hazırlık",
  "lib.programs": "Program",
  "lib.viewReport": "Raporu Görüntüle",
  "lib.markConverted": "Dönüştü olarak işaretle (takipleri durdurur)",
  "lib.markNotConverted": "Dönüşmedi olarak işaretle",
  "lib.loadFailed": "Adaylar yüklenemedi",
  "lib.deleted": "Aday silindi",
  "lib.deleteFailed": "Silme başarısız",
  "lib.updateFailed": "Güncelleme başarısız",
  "lib.markedConverted": "Dönüştü olarak işaretlendi",
  "lib.markedNot": "Dönüşmedi olarak işaretlendi",
  "lib.exported": "{n} aday CSV olarak indirildi",

  "cmp.emptyTitle": "Karşılaştırılacak bir şey yok",
  "cmp.emptyDesc": "Adaylar sekmesine gidin, iki adayı seçin ve yan yana görmek için Karşılaştır'a tıklayın.",
  "cmp.company": "Şirket {letter}",
  "cmp.kpiReadiness": "Kurum Hazırlığı",
  "cmp.kpiHigh": "Yüksek Öncelikli Program",
  "cmp.avgTitle": "5D Faz Ortalamaları",
  "cmp.avgSub": "Tüm seviyelerde faz başına ortalama puan (0-5)",
  "cmp.byLevelTitle": "Seviyeye Göre Hazırlık",
  "cmp.byLevelSub": "İki karne de {model} modelini kullanıyor — seviye seviye karşılaştırma",
  "cmp.level": "Seviye",
  "cmp.delta": "Fark (B−A)",
  "cmp.diffModels": "Bu karneler farklı kıdem modelleri kullanıyor ({a} / {b}), bu yüzden seviye bazlı karşılaştırma yapılamıyor. Yukarıdaki yetkinlik ortalamaları doğrudan karşılaştırılabilir.",

  "set.accessTitle": "Sahip Erişimi",
  "set.accessDesc": "Marka ayarlarını düzenlemek için yönetici şifresini girin",
  "set.backToApp": "Uygulamaya dön",
  "set.title": "Marka Ayarları",
  "set.subtitle": "Değerlendirmenizi kişiselleştirin",
  "set.save": "Değişiklikleri kaydet",
  "set.saved": "Ayarlar kaydedildi",
  "set.saveFailed": "Ayarlar kaydedilemedi",
  "set.unauthorized": "Yetkisiz — şifreyi kontrol edin",
  "set.loading": "Yükleniyor…",
  "set.identity": "Kimlik",
  "set.brandName": "Marka adı",
  "set.logo": "Logo",
  "set.noLogo": "Logo yok",
  "set.upload": "Yükle",
  "set.logoHint": "PNG, JPG, SVG veya WebP · en fazla 5MB",
  "set.logoUploaded": "Logo yüklendi",
  "set.logoFailed": "Logo yüklenemedi",
  "set.brandColor": "Marka rengi",
  "set.landingCopy": "Açılış sayfası metni",
  "set.introVideo": "Tanıtım videosu bağlantısı",
  "set.introVideoHint": "YouTube, Vimeo veya MP4 bağlantısı yapıştırın. Açılış sayfasındaki animasyonlu \"Nasıl çalışır?\" adımlarının yerini alır. Boş bırakırsanız animasyonlu sürüm kullanılır.",
  "set.headline": "Başlık",
  "set.subheadline": "Alt başlık",
  "set.reportEmail": "Rapor e-postası",
  "set.previewEmail": "E-postayı önizle",
  "set.previewFailed": "E-posta önizlemesi yüklenemedi",
  "set.intro": "Giriş mesajı",
  "set.introHint": "Rapor e-postasının üstünde görünür. {name} ve {company} yer tutucularını kullanabilirsiniz.",
  "set.bookingLink": "Randevu bağlantısı",
  "set.bookingHint": "Rapor e-postalarına, takip e-postalarına ve rapor sayfasına \"Görüşme planla\" butonu olarak eklenir.",
  "set.followupsToggle": "Dönüşmeyen adaylara otomatik 3. gün / 7. gün takip e-postaları gönder",
  "set.followups": "Takip e-postaları",
  "set.followupsHint": "Dönüştü olarak işaretlenmemiş adaylara gönderilir. {name}, {company} ve {gap} yer tutucularını kullanın. \"Görüşme planla\" butonu otomatik eklenir.",
  "set.day3": "3. gün hatırlatması",
  "set.day7": "7. gün hatırlatması",
  "set.preview": "Önizle",
  "set.subjectPh": "Konu satırı",
  "set.bodyPh": "E-posta metni",
  "set.livePreview": "Canlı önizleme",
  "set.previewBadge": "Ücretsiz Karne",
  "set.previewHeadlinePh": "Başlığınız buraya",
  "set.previewSubPh": "Alt başlığınız buraya",
  "set.previewCta": "Ücretsiz değerlendirmeye başla",
  "set.emailPreviewTitle": "E-posta önizlemesi",
  "set.emailPreviewNote": "Önizleme örnek veri kullanır (Jane Doe / Acme Corporation). Gerçek e-postalar her adayın kendi sonuçlarını içerir.",
  "set.langEn": "İngilizce",
  "set.langTr": "Türkçe",
};

const DICT = { en, tr };

const LangCtx = createContext({ lang: "tr", setLang: () => {}, t: (k) => k });

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "tr";
    const fromUrl = new URLSearchParams(window.location.search).get("lang");
    if (fromUrl === "en" || fromUrl === "tr") {
      localStorage.setItem(STORE_KEY, fromUrl);
      return fromUrl;
    }
    const saved = localStorage.getItem(STORE_KEY);
    return saved === "en" || saved === "tr" ? saved : "tr";
  });

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(() => {
    const t = (key, vars) => {
      let s = DICT[lang]?.[key] ?? DICT.en[key] ?? key;
      if (vars) {
        Object.entries(vars).forEach(([k, v]) => {
          s = s.split(`{${k}}`).join(String(v));
        });
      }
      return s;
    };
    const setLang = (l) => {
      localStorage.setItem(STORE_KEY, l);
      setLangState(l);
    };
    return { lang, setLang, t, locale: lang === "tr" ? "tr-TR" : "en-US" };
  }, [lang]);

  return <LangCtx.Provider value={value}>{children}</LangCtx.Provider>;
}

export const useI18n = () => useContext(LangCtx);
